import React, { useState } from "react";
import { Plus, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { useAgents } from "@/shared/api/agents";
import { personName } from "@/shared/api/members";
import { useSettings } from "@/shared/api/settings";
import { isSkillActionLive, useAgentSkills, useRemoveSkill, useUpdateSkillLink } from "@/shared/api/skills";
import { Counted, ListCard, ListEmpty, ListRow, ListTile } from "@/shared/components/ListCard";
import { AuditShield, SourceBadge } from "@/shared/components/skill-atoms";
import { BrandButton, Chip, GhostButton, IconButton } from "@/shared/components/ui-kit";
import SkillDialog from "../SkillDialog";
import SkillOwner from "./SkillOwner";
import Section from "./Section";

import type { ConfigSection } from "../ConfigSectionHelp";
import type { Agent, AgentSkill, PersonRef } from "@/shared/types";

/* The agent's skills (backend/modules/skills/README.md, "A skill on an agent"): who owns the agent,
   the skills waiting for the owner's yes, and the ones attached. The rules are the API's;
   this only shows the controls a person may use. The owner attaches and approves; anyone
   else who can edit agents (admins) asks, and removes; everyone else reads. */

/** A link's version is the folder's git tree sha: shown short, like a commit. */
const shortVersion = (v: string) => (/^[0-9a-f]{12,}$/i.test(v) ? v.slice(0, 7) : `v${v}`);

/** Who asked for a skill, as the row says it. */
const asker = (person: PersonRef | null, me: string | undefined) =>
  !person ? "someone" : person.id === me ? "you" : personName(person);

const letterTile = (name: string) => (
  <ListTile><span className="text-[12px] font-mono font-semibold uppercase">{name[0]}</span></ListTile>
);

const skillTitle = (skill: AgentSkill) => (
  <span className="inline-flex items-center gap-1.5 max-w-full">
    <span className="truncate">{skill.link.name}</span>
    <SourceBadge source={skill.source} />
  </span>
);

export default function SkillsSection({ agent, onHelp }: {
  agent: Agent;
  onHelp: (section: ConfigSection) => void;
}) {
  return (
    <Section section="skills" onHelp={onHelp}>
      <AgentSkills agent={agent} />
    </Section>
  );
}

function AgentSkills({ agent }: { agent: Agent }) {
  const { data: settings } = useSettings();
  const { data: rows } = useAgents();
  const { data: skills, isPending } = useAgentSkills(agent.id);
  const remove = useRemoveSkill();
  const update = useUpdateSkillLink();
  const updateLive = isSkillActionLive("takeUpdate");
  const [dialog, setDialog] = useState<{ approving?: AgentSkill; updating?: AgentSkill } | null>(null);

  const row = rows?.find((r) => r.id === agent.id);
  const owner = row?.owner ?? null;
  const role = settings?.org?.role;
  const isAdmin = role === "owner" || role === "admin";
  const isOwner = !!owner && owner.id === settings?.user.id;
  const mayEdit = isOwner || isAdmin;
  const sandboxOn = !!agent.capabilities?.sandboxEnabled;
  const all = skills ?? [];
  const attached = all.filter((s) => s.link.status === "attached");
  const requests = all.filter((s) => s.link.status === "requested");

  const drop = (skill: AgentSkill, done: string) =>
    remove.mutate({ skillId: skill.id, agentId: agent.id }, { onSuccess: () => toast.success(done) });
  // a recipe's new steps drive a signed-in browser: the owner reads them first
  const takeUpdate = (skill: AgentSkill) => skill.recipe ? setDialog({ updating: skill }) :
    update.mutate({ skillId: skill.id, agentId: agent.id, version: skill.version }, {
      onSuccess: (link) => toast.success(`${link.name} updated to ${shortVersion(link.version)} — publish to let chats use it`),
    });

  return (
    <div className="space-y-4">
      {row && <SkillOwner agentId={agent.id} owner={owner} isYou={isOwner} canChange={isAdmin} />}

      {requests.length > 0 && (
        <ListCard label={<><Counted n={requests.length} noun="request" /> · waiting for {isOwner ? "you" : owner ? personName(owner) : "an owner"}</>}>
          {requests.map((s) => (
            <ListRow
              key={s.id}
              rowTitle={s.link.description}
              lead={letterTile(s.link.name)}
              title={skillTitle(s)}
              desc={`Asked by ${asker(s.link.requested_by, settings?.user.id)} · ${s.link.description}`}
            >
              <AuditShield audits={s.audits} />
              {isOwner ? (
                <>
                  <GhostButton className="h-7 px-2.5" disabled={remove.isPending} onClick={() => drop(s, `Turned down ${s.link.name}`)}>Deny</GhostButton>
                  <BrandButton className="h-7 px-2.5" onClick={() => setDialog({ approving: s })}>Approve</BrandButton>
                </>
              ) : (
                <>
                  <span className="shrink-0 hidden sm:inline whitespace-nowrap text-[12px] text-[var(--text-tertiary)]">
                    Waiting for {owner ? personName(owner) : "an owner"}
                  </span>
                  {mayEdit && (
                    <IconButton disabled={remove.isPending} onClick={() => drop(s, `Withdrew the request for ${s.link.name}`)} title="Withdraw the request" className="hidden group-hover:flex hover:text-[var(--danger)]">
                      <Trash2 className="w-3 h-3" strokeWidth={1.75} />
                    </IconButton>
                  )}
                </>
              )}
            </ListRow>
          ))}
        </ListCard>
      )}

      <ListCard
        label={isPending ? "Loading skills…" : <Counted n={attached.length} noun="skill" />}
        action={mayEdit && (
          <GhostButton
            onClick={() => setDialog({})}
            disabled={!owner}
            title={owner ? undefined : "No owner yet: an admin assigns one first"}
            className="h-7 px-2.5 disabled:opacity-40 disabled:pointer-events-none"
          >
            <Plus className="w-3.5 h-3.5" strokeWidth={1.75} /> Add skills
          </GhostButton>
        )}
      >
        {!isPending && attached.length === 0 && (
          <ListEmpty
            icon={Sparkles}
            title="No skills yet"
            hint={`A skill teaches the agent a procedure — writing a release note, triaging a bug — that it loads when a task matches.${
              mayEdit ? "" : ` Only ${owner ? personName(owner) : "its owner"} or an admin can add one.`}`}
          />
        )}
        {attached.map((s) => (
          <ListRow key={s.id} rowTitle={s.link.description} lead={letterTile(s.link.name)} title={skillTitle(s)} desc={s.link.description}>
            {s.link.update_available && (isOwner ? (
              <span title={updateLive ? undefined : "Taking an update in one click isn't available yet: remove the skill and add it again to take it"}>
                <GhostButton
                  className="h-7 px-2.5"
                  disabled={update.isPending || !updateLive}
                  onClick={() => takeUpdate(s)}
                  title={`A newer version than ${shortVersion(s.link.version)} is out: take it onto the draft`}
                >
                  Update available
                </GhostButton>
              </span>
            ) : (
              <span title={`A newer version is out; ${owner ? personName(owner) : "the agent's owner"} decides whether the agent takes it`}>
                <Chip tone="warn">Update waiting<span className="hidden sm:inline">&nbsp;for {owner ? personName(owner) : "the owner"}</span></Chip>
              </span>
            ))}
            {s.link.needs_sandbox && !sandboxOn && (
              <span title="It ships scripts, and the sandbox is off: the agent gets the instructions only">
                <Chip>Needs a sandbox</Chip>
              </span>
            )}
            <span title={`Version ${s.link.version}`} className="shrink-0 hidden sm:inline text-[11.5px] font-mono tabular-nums text-[var(--text-quaternary)]">
              {shortVersion(s.link.version)}
            </span>
            <AuditShield audits={s.audits} />
            {mayEdit && (
              <IconButton disabled={remove.isPending} onClick={() => drop(s, `${s.link.name} removed — publish to take it off chats`)} title="Remove skill" className="hidden group-hover:flex hover:text-[var(--danger)]">
                <Trash2 className="w-3 h-3" strokeWidth={1.75} />
              </IconButton>
            )}
          </ListRow>
        ))}
      </ListCard>

      {dialog && owner && (
        <SkillDialog
          agentId={agent.id}
          sandboxOn={sandboxOn}
          owner={owner}
          isOwner={isOwner}
          skills={all}
          approving={dialog.approving}
          updating={dialog.updating}
          onClose={() => setDialog(null)}
        />
      )}
    </div>
  );
}
